import { useState, useEffect } from 'react';
import { useOutletContext, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Users, Trophy, Star, Shield, Target,
  Calendar, ChevronRight, Award, UserCog, BarChart3
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const levelColors = {
  focus: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/20' },
  developing: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/20' },
  proficient: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/20' },
};

const TeamOverview = () => {
  const { club } = useOutletContext();
  const { teamId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (club?.id && teamId) fetchOverview();
  }, [club, teamId]);

  const fetchOverview = async () => {
    const token = localStorage.getItem('club_token');
    try {
      const res = await axios.get(`${API}/clubs/teams/${teamId}/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
    catch { return d; }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return (
    <div className="text-center py-20">
      <p className="text-muted-foreground">Team not found</p>
      <Link to={`/club/${club?.id}/teams`} className="text-primary text-sm hover:underline mt-2 inline-block">Back to Teams</Link>
    </div>
  );

  const { team, athletes, match_record, coaches, managers, matches, scheduled_matches } = data;
  const rec = match_record;

  return (
    <div className="space-y-5" data-testid="team-overview">
      <Link to={`/club/${club?.id}`}
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm"
      >
        <ArrowLeft size={16} /> Back to Dashboard
      </Link>

      {/* Team Header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="bg-card border border-border/40 rounded-sm p-6"
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="w-14 h-14 rounded-sm bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Shield size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <h1 className="font-heading text-2xl md:text-3xl uppercase tracking-wide">{team.name}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
              {team.category && <span className="text-primary font-medium">{team.category}</span>}
              <span>{data.athlete_count} athletes</span>
              <span>Avg Score: <span className="text-primary">{data.avg_score || '—'}</span></span>
            </div>
          </div>
          {/* Match Record */}
          {rec.total > 0 && (
            <div className="flex items-center gap-3 p-3 bg-zinc-900/50 border border-border/20 rounded-sm">
              <div className="text-center px-2">
                <p className="font-heading text-xl text-emerald-400">{rec.wins}</p>
                <p className="text-[9px] uppercase text-muted-foreground">Wins</p>
              </div>
              <div className="text-center px-2">
                <p className="font-heading text-xl text-amber-400">{rec.draws}</p>
                <p className="text-[9px] uppercase text-muted-foreground">Draws</p>
              </div>
              <div className="text-center px-2">
                <p className="font-heading text-xl text-red-400">{rec.losses}</p>
                <p className="text-[9px] uppercase text-muted-foreground">Losses</p>
              </div>
              <div className="text-center px-2 border-l border-border/30">
                <p className="font-heading text-xl">{rec.total}</p>
                <p className="text-[9px] uppercase text-muted-foreground">Total</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Staff */}
      {(coaches.length > 0 || managers.length > 0) && (
        <div className="grid md:grid-cols-2 gap-3">
          {coaches.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="bg-card border border-border/40 rounded-sm p-4"
            >
              <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                <UserCog size={13} className="text-primary" /> Coaches
              </h2>
              <div className="space-y-2">
                {coaches.map(c => (
                  <div key={c.id} className="flex items-center gap-3 p-2 bg-zinc-900/40 rounded-sm border border-border/10">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                      {c.name?.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm">{c.name}</p>
                      <p className="text-[10px] text-muted-foreground capitalize">{c.role?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
          {managers.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
              className="bg-card border border-border/40 rounded-sm p-4"
            >
              <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                <Users size={13} className="text-emerald-400" /> Managers
              </h2>
              <div className="space-y-2">
                {managers.map(m => (
                  <div key={m.id} className="flex items-center gap-3 p-2 bg-zinc-900/40 rounded-sm border border-border/10">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center text-xs font-bold text-emerald-400">
                      {m.name?.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm">{m.name}</p>
                      <p className="text-[10px] text-muted-foreground">{m.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* Athletes Table */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-card border border-border/40 rounded-sm overflow-hidden"
      >
        <div className="p-4 border-b border-border/30 flex items-center justify-between">
          <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <Users size={13} className="text-primary" /> Squad ({athletes.length})
          </h2>
        </div>
        {athletes.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-border/30 bg-zinc-900/50">
                  <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-widest text-muted-foreground">Player</th>
                  <th className="px-3 py-2.5 text-center text-[10px] uppercase tracking-widest text-muted-foreground">Age</th>
                  <th className="px-3 py-2.5 text-center text-[10px] uppercase tracking-widest text-muted-foreground">Position</th>
                  <th className="px-3 py-2.5 text-center text-[10px] uppercase tracking-widest text-muted-foreground">Score</th>
                  <th className="px-3 py-2.5 text-center text-[10px] uppercase tracking-widest text-muted-foreground">Level</th>
                  <th className="px-3 py-2.5 text-center text-[10px] uppercase tracking-widest text-muted-foreground">Matches</th>
                  <th className="px-3 py-2.5 text-center text-[10px] uppercase tracking-widest text-muted-foreground">Goals</th>
                  <th className="px-3 py-2.5 text-center text-[10px] uppercase tracking-widest text-muted-foreground">Assists</th>
                </tr>
              </thead>
              <tbody>
                {athletes.map(a => {
                  const lc = levelColors[a.level] || {};
                  return (
                    <tr key={a.athlete_id} className="border-b border-border/10 hover:bg-zinc-800/20 transition-colors">
                      <td className="px-4 py-3">
                        <Link to={`/club/${club?.id}/intelligence/player/${a.athlete_id}`}
                          className="flex items-center gap-2.5 hover:text-primary transition-colors"
                          data-testid={`athlete-${a.athlete_id}`}
                        >
                          <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-primary shrink-0">
                            {a.name?.charAt(0)}
                          </div>
                          <span className="text-sm">{a.name}</span>
                        </Link>
                      </td>
                      <td className="px-3 py-3 text-center text-sm text-muted-foreground">{a.age || '—'}</td>
                      <td className="px-3 py-3 text-center text-xs text-muted-foreground">{a.position || '—'}</td>
                      <td className="px-3 py-3 text-center">
                        <span className={`font-heading text-sm ${a.overall_score >= 8.1 ? 'text-emerald-400' : a.overall_score >= 6.1 ? 'text-amber-400' : a.overall_score > 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                          {a.overall_score || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        {a.level ? (
                          <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-sm border ${lc.bg} ${lc.text} ${lc.border}`}>
                            {a.level}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-3 text-center text-sm text-muted-foreground">{a.matches_played}</td>
                      <td className="px-3 py-3 text-center">
                        <span className={`font-mono text-sm ${a.goals > 0 ? 'text-primary' : 'text-zinc-600'}`}>{a.goals}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`font-mono text-sm ${a.assists > 0 ? 'text-emerald-400' : 'text-zinc-600'}`}>{a.assists}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-muted-foreground text-sm">No athletes in this team</div>
        )}
      </motion.div>

      {/* Match History */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className="bg-card border border-border/40 rounded-sm p-4"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <Trophy size={13} className="text-primary" /> Match Results ({matches.length})
          </h2>
        </div>
        {matches.length > 0 ? (
          <div className="space-y-2">
            {matches.map(m => (
              <Link key={m.id} to={`/club/${club?.id}/match-history/${m.id}`}
                className="flex items-center gap-4 p-3 bg-zinc-900/40 rounded-sm border border-border/10 hover:border-primary/20 transition-colors group"
                data-testid={`match-${m.id}`}
              >
                <div className={`w-10 h-10 rounded-sm flex flex-col items-center justify-center border shrink-0 ${
                  m.result === 'win' ? 'bg-emerald-500/10 border-emerald-500/20' :
                  m.result === 'loss' ? 'bg-red-500/10 border-red-500/20' :
                  'bg-amber-500/10 border-amber-500/20'
                }`}>
                  <span className="font-heading text-sm leading-none">{m.our_score}-{m.opponent_score}</span>
                  <span className={`text-[7px] uppercase ${
                    m.result === 'win' ? 'text-emerald-400' : m.result === 'loss' ? 'text-red-400' : 'text-amber-400'
                  }`}>{m.result}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm group-hover:text-primary transition-colors">vs {m.opponent}</p>
                  <div className="flex gap-3 text-[10px] text-muted-foreground mt-0.5">
                    <span className="flex items-center gap-1"><Calendar size={9} /> {formatDate(m.match_date)}</span>
                    <span>{m.player_count} players</span>
                  </div>
                </div>
                {m.avg_player_score > 0 && (
                  <span className={`font-heading text-sm ${m.avg_player_score >= 8 ? 'text-emerald-400' : m.avg_player_score >= 6 ? 'text-amber-400' : 'text-red-400'}`}>
                    {m.avg_player_score.toFixed(1)}
                  </span>
                )}
                <ChevronRight size={14} className="text-zinc-600 group-hover:text-primary" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground text-sm">
            No match reports for this team
          </div>
        )}
      </motion.div>

      {/* Scheduled Matches */}
      {scheduled_matches.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-card border border-border/40 rounded-sm p-4"
        >
          <h2 className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
            <Calendar size={13} className="text-emerald-400" /> Upcoming Matches
          </h2>
          <div className="space-y-2">
            {scheduled_matches.map(s => (
              <div key={s.id} className="flex items-center gap-3 p-3 bg-zinc-900/40 rounded-sm border border-border/10">
                <Calendar size={14} className="text-primary shrink-0" />
                <div>
                  <p className="text-sm">vs {s.opponent}</p>
                  <p className="text-[10px] text-muted-foreground">{formatDate(s.match_date)} {s.location && `· ${s.location}`}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default TeamOverview;
